/**
 * VAL — Virtual AI Assistant Lookup.
 *
 * Floating assistant available on every /app screen, and the full-page
 * "Ask Val…" command center. Users ask in plain text or by voice; Val
 * answers, finds opportunities, delegates to her specialist teammates,
 * and walks users to the right screen (the server returns validated
 * navigation actions — the model never invents routes). Voice in/out uses
 * ElevenLabs/Gemini TTS server-side with browser Web Speech fallback.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { starterPromptsForPath } from '../lib/valStarters';
import { ai } from '../api/org';
import { HttpError } from '../api/client';
import type { ConciergeClientAction, ConciergeHistoryItem } from '../api/types';
import { formatDate, opportunityValueLabel } from '../lib/format';
import { MicIcon, SpeakerIcon, SpeakerOffIcon } from './icons';

interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  actions?: ConciergeClientAction[];
}

export type ConciergeMode = 'standard' | 'onboard';

// R2-BR-D05: starter chips are workspace-aware — see lib/valStarters
// (Individual workspaces no longer get org-product prompts like SBIR).

function hasSpeechRecognition(): boolean {
  const w = window as unknown as Record<string, unknown>;
  return typeof w.SpeechRecognition === 'function' || typeof w.webkitSpeechRecognition === 'function';
}

function hasSpeechSynthesis(): boolean {
  return 'speechSynthesis' in window;
}

let currentAudio: HTMLAudioElement | null = null;

function stopAudio() {
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.src = '';
    currentAudio = null;
  }
}

/** Browser built-in voice — fallback when Val's premium voice is unavailable. */
function speakBrowser(text: string) {
  if (!hasSpeechSynthesis()) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text.slice(0, 600));
  u.rate = 1;
  u.pitch = 1;
  window.speechSynthesis.speak(u);
}

function stopSpeaking() {
  stopAudio();
  if (hasSpeechSynthesis()) window.speechSynthesis.cancel();
}

/**
 * Val's voice: premium TTS first, browser synthesis fallback.
 * Fire-and-forget — never blocks the chat on audio failure.
 */
async function speakVal(text: string) {
  stopSpeaking();
  try {
    const blob = await ai.speak(text.slice(0, 600));
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    currentAudio = audio;
    audio.onended = () => {
      URL.revokeObjectURL(url);
      if (currentAudio === audio) currentAudio = null;
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      if (currentAudio === audio) currentAudio = null;
      speakBrowser(text);
    };
    await audio.play();
  } catch {
    speakBrowser(text);
  }
}

function profileValueText(v: string | number | string[]): string {
  return Array.isArray(v) ? v.join(', ') : String(v);
}

function ActionCards({
  actions,
  onProfileChoice,
}: {
  actions: ConciergeClientAction[];
  onProfileChoice?: (text: string) => void;
}) {
  const navigate = useNavigate();
  return (
    <div className="concierge-actions">
      {actions.map((a, i) => {
        if (a.kind === 'navigate') {
          return (
            <button
              key={i}
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => navigate(a.route)}
            >
              Take me to {a.label} →
            </button>
          );
        }
        if (a.kind === 'opportunities') {
          return (
            <div key={i} className="concierge-cards" role="list" aria-label="Found opportunities">
              {a.items.map((o) => (
                <Link key={o.id} to={`/app/opportunities/${o.id}`} className="concierge-card" role="listitem">
                  <strong>{o.title}</strong>
                  <span className="text-sm text-muted">
                    {[o.agency, opportunityValueLabel(o, '') || null]
                      .filter(Boolean)
                      .join(' · ') || 'Details inside'}
                  </span>
                  {o.responseDeadline && (
                    <span className="text-sm">Due {formatDate(o.responseDeadline)}</span>
                  )}
                </Link>
              ))}
            </div>
          );
        }
        if (a.kind === 'profile_conflict') {
          // RC-2026-10-26.12 (D81 r3): the server's structured conflict
          // payload — current vs proposed with the three choices. The
          // buttons send the canonical choice phrases the server's
          // deterministic router resolves; typing a choice works too.
          return (
            <div
              key={i}
              className="concierge-card concierge-conflict"
              role="group"
              aria-label={`Change your ${a.label}? Nothing is saved until you choose.`}
            >
              <strong>{a.label}</strong>
              <span className="text-sm">
                <span className="text-muted">Current:</span> {profileValueText(a.currentValue)}
              </span>
              <span className="text-sm">
                <span className="text-muted">New:</span> {profileValueText(a.proposedValue)}
              </span>
              <span className="text-sm text-muted">Nothing is saved until you choose:</span>
              <div className="row" style={{ gap: 'var(--s2)', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => onProfileChoice?.('Replace — use the new answer')}
                >
                  Use the new answer
                </button>
                {a.canAppend && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => onProfileChoice?.('Add the new answer as a dated note')}
                  >
                    Add as a dated note
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => onProfileChoice?.('Keep the current value')}
                >
                  Keep current value
                </button>
              </div>
            </div>
          );
        }
        if (a.kind === 'profile_notice') {
          const text =
            a.status === 'saved'
              ? `✓ ${a.label} updated`
              : a.status === 'kept'
                ? `${a.label} left unchanged`
                : a.status === 'refused'
                  ? 'Profile changes are admin-only — nothing was saved'
                  : `No change needed — ${a.label} already up to date`;
          return (
            <p key={i} className="text-sm text-muted" role="status">
              {text}
            </p>
          );
        }
        return (
          <div key={i} className="concierge-cards" role="list" aria-label="Upcoming deadlines">
            {a.items.map((d) => (
              <Link key={d.id} to={`/app/opportunities/${d.id}`} className="concierge-card" role="listitem">
                <strong>{d.title}</strong>
                <span className="text-sm">
                  Due {formatDate(d.responseDeadline)} · {d.daysLeft === 0 ? 'today' : `${d.daysLeft} days left`}
                </span>
              </Link>
            ))}
          </div>
        );
      })}
    </div>
  );
}

export interface ConciergeThreadProps {
  mode: ConciergeMode;
  /** Rendered inline on a page instead of inside the floating panel. */
  pageMode?: boolean;
  /** Lets a parent send messages into the thread (e.g. specialist cards). */
  sendRef?: MutableRefObject<((text: string) => void) | null>;
  /** Sent once when the thread mounts (e.g. "Ask Val…" entry points). */
  initialMessage?: string | null;
  /** R2-BR-D04: opportunity the user is viewing — grounds Val's turn and
   *  any specialist handoff in that record's real deadline. */
  opportunityId?: string | null;
  onClose?: () => void;
}

export function ConciergeThread({ mode, pageMode, sendRef, initialMessage, opportunityId, onClose }: ConciergeThreadProps) {
  const location = useLocation();
  const starters = starterPromptsForPath(location.pathname);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idRef = useRef(1);
  const threadRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<{ stop: () => void } | null>(null);

  const voiceSupported = hasSpeechRecognition();
  const ttsSupported = hasSpeechSynthesis();

  // Auto-scroll the thread on new messages.
  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  useEffect(() => {
    window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => {
      stopSpeaking();
      recognitionRef.current?.stop();
    };
  }, []);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || sending) return;
      setError(null);
      const userMsg: ChatMessage = { id: idRef.current++, role: 'user', text: trimmed };
      const history: ConciergeHistoryItem[] = [...messages, userMsg]
        .slice(-10)
        .map((m) => {
          // RC-2026-10-26.12 (D81 r3): echo a presented profile
          // conflict back with its assistant message so the server
          // can resolve the user's choice deterministically.
          const conflict =
            m.role === 'assistant'
              ? m.actions?.find((a) => a.kind === 'profile_conflict')
              : undefined;
          return {
            role: m.role,
            content: m.text.slice(0, 1500),
            ...(conflict && conflict.kind === 'profile_conflict'
              ? {
                  profileConflict: {
                    field: conflict.field,
                    label: conflict.label,
                    currentValue: conflict.currentValue,
                    proposedValue: conflict.proposedValue,
                    canAppend: conflict.canAppend,
                  },
                }
              : {}),
          };
        });
      setMessages((prev) => [...prev, userMsg]);
      setInput('');
      setSending(true);
      try {
        const turn = await ai.concierge(trimmed, history, mode, voiceMode, opportunityId ?? null);
        const assistantMsg: ChatMessage = {
          id: idRef.current++,
          role: 'assistant',
          text: turn.reply,
          actions: turn.actions,
        };
        setMessages((prev) => [...prev, assistantMsg]);
        if (voiceMode && ttsSupported) void speakVal(turn.spoken || turn.reply);
      } catch (err) {
        if (err instanceof HttpError && err.status === 503) {
          setError(
            'Val is not connected yet — the workspace needs an AI key before I can chat. Everything else in the platform keeps working.',
          );
        } else if (
          err instanceof HttpError &&
          (err.status === 403 || err.code === 'ORGANIZATION_ONLY')
        ) {
          setError(
            'Val’s full workspace tools are part of organization plans — but I can still help with general questions here.',
          );
        } else {
          setError(err instanceof Error ? err.message : 'Something went wrong. Try again.');
        }
      } finally {
        setSending(false);
      }
    },
    [messages, sending, voiceMode, ttsSupported, mode, opportunityId],
  );

  // Expose send() to the parent (specialist cards, guided flows).
  useEffect(() => {
    if (sendRef) sendRef.current = (text: string) => void send(text);
    return () => {
      if (sendRef) sendRef.current = null;
    };
  }, [send, sendRef]);

  // Fire an initial message once (e.g. "Ask Val…" buttons route here).
  const initialSentRef = useRef(false);
  useEffect(() => {
    if (initialMessage && !initialSentRef.current) {
      initialSentRef.current = true;
      void send(initialMessage);
    }
  }, [initialMessage, send]);

  const toggleListening = useCallback(() => {
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const w = window as unknown as Record<string, new () => {
      lang: string;
      interimResults: boolean;
      maxAlternatives: number;
      onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
      onerror: (() => void) | null;
      onend: (() => void) | null;
      start: () => void;
      stop: () => void;
    }>;
    const Rec = w.SpeechRecognition ?? w.webkitSpeechRecognition;
    if (!Rec) {
      setError('Voice input is not supported in this browser — type your question instead.');
      return;
    }
    setError(null);
    const rec = new Rec();
    rec.lang = 'en-US';
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      const transcript = e.results[0]?.[0]?.transcript ?? '';
      setListening(false);
      if (transcript.trim()) {
        setVoiceMode(true);
        void send(transcript);
      }
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    setListening(true);
    try {
      rec.start();
    } catch {
      setListening(false);
    }
  }, [listening, send]);

  const toggleVoiceMode = useCallback(() => {
    setVoiceMode((v) => {
      if (v) stopSpeaking();
      return !v;
    });
  }, []);

  return (
    <div className={`concierge-thread-wrap${pageMode ? ' concierge-thread-page' : ''}`}>
      {!pageMode && (
        <header className="concierge-head">
          <div>
            <strong>Val</strong>{' '}
            <span className="text-sm text-muted">
              · {mode === 'onboard' ? 'guided profile setup' : 'your Navigator assistant'}
            </span>
          </div>
          <div className="row" style={{ gap: 'var(--s2)' }}>
            {ttsSupported && (
              <button
                type="button"
                className={`btn btn-ghost btn-sm${voiceMode ? ' is-active' : ''}`}
                onClick={toggleVoiceMode}
                aria-pressed={voiceMode}
                title={voiceMode ? 'Turn voice replies off' : 'Turn voice replies on'}
              >
                {voiceMode ? <SpeakerIcon size={16} /> : <SpeakerOffIcon size={16} />} Voice
              </button>
            )}
            {onClose && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={onClose}
                aria-label="Close concierge"
              >
                ✕
              </button>
            )}
          </div>
        </header>
      )}

      <div ref={threadRef} className="concierge-thread" aria-live="polite">
        {messages.length === 0 && mode === 'onboard' && (
          <div className="concierge-welcome">
            <p>
              <strong>Let's get you set up.</strong> I'll walk you through your
              organization profile one quick question at a time — a complete
              profile means better-fit opportunities. Say <em>skip</em> any time
              to move past a question.
            </p>
            <div className="chip-row">
              <button type="button" className="filter-chip" onClick={() => void send('Let\'s start my guided setup')}>
                ✦ Start guided setup
              </button>
            </div>
          </div>
        )}
        {messages.length === 0 && mode === 'standard' && !pageMode && (
          <div className="concierge-welcome">
            <p>
              <strong>Hi, I'm Val.</strong> Ask me about the platform, have me find a
              grant or RFP, or tell me where you want to go — I'll walk you there.
            </p>
            <div className="chip-row">
              {starters.map((s) => (
                <button key={s} type="button" className="filter-chip" onClick={() => void send(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`concierge-msg concierge-msg-${m.role}`}>
            <div className="concierge-bubble">{m.text}</div>
            {m.role === 'assistant' && m.actions && m.actions.length > 0 && (
              <ActionCards actions={m.actions} onProfileChoice={(text) => void send(text)} />
            )}
          </div>
        ))}
        {sending && (
          <div className="concierge-msg concierge-msg-assistant">
            <div className="concierge-bubble concierge-thinking" aria-label="Val is thinking">
              <span aria-hidden="true">● ● ●</span>
            </div>
          </div>
        )}
        {error && (
          <div className="concierge-error" role="alert">
            {error}
          </div>
        )}
      </div>

      <form
        className="concierge-input-row"
        onSubmit={(e) => {
          e.preventDefault();
          void send(input);
        }}
      >
        {voiceSupported && (
          <button
            type="button"
            className={`btn btn-ghost btn-sm concierge-mic${listening ? ' is-listening' : ''}`}
            onClick={toggleListening}
            aria-pressed={listening}
            aria-label={listening ? 'Stop listening' : 'Ask by voice'}
            title="Ask by voice"
          >
            <MicIcon size={18} />
          </button>
        )}
        <input
          ref={inputRef}
          className="input concierge-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={listening ? 'Listening…' : 'Ask Val anything…'}
          aria-label="Message Val"
          maxLength={2000}
          disabled={listening}
        />
        <button type="submit" className="btn btn-primary btn-sm" disabled={sending || !input.trim()}>
          Send
        </button>
      </form>
      {listening && (
        <p className="text-sm text-muted concierge-listening" role="status">
          <span className="concierge-listening-ico" aria-hidden="true"><MicIcon size={14} /></span>
          {' '}Listening — speak now, I'll send it when you pause.
        </p>
      )}
      {pageMode && ttsSupported && (
        <div className="row" style={{ padding: '0 var(--s4) var(--s3)', gap: 'var(--s2)' }}>
          <button
            type="button"
            className={`btn btn-ghost btn-sm${voiceMode ? ' is-active' : ''}`}
            onClick={toggleVoiceMode}
            aria-pressed={voiceMode}
          >
            {voiceMode ? <SpeakerIcon size={16} /> : <SpeakerOffIcon size={16} />}{' '}
            {voiceMode ? 'Voice replies on' : 'Voice replies off'}
          </button>
        </div>
      )}
    </div>
  );
}

/** Floating Val button + panel, mounted on every /app screen. */
export function Concierge() {
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [mode, setMode] = useState<ConciergeMode>('standard');
  const [initialMessage, setInitialMessage] = useState<string | null>(null);
  const [opportunityId, setOpportunityId] = useState<string | null>(null);
  const fabRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(false);

  // External open signal: window.dispatchEvent(new CustomEvent('vac:open', {detail: {mode: 'onboard'}}))
  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent).detail as { mode?: ConciergeMode; initialMessage?: string; opportunityId?: string | null } | undefined;
      setMode(detail?.mode === 'onboard' ? 'onboard' : 'standard');
      setInitialMessage(detail?.initialMessage ?? null);
      setOpportunityId(detail?.opportunityId ?? null);
      setHasOpened(true);
      setOpen(true);
    };
    window.addEventListener('vac:open', onOpen);
    return () => window.removeEventListener('vac:open', onOpen);
  }, []);

  const openStandard = useCallback(() => {
    setMode('standard');
    setInitialMessage(null);
    setOpportunityId(null);
    setHasOpened(true);
    setOpen((o) => !o);
  }, []);

  // Esc closes the panel.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open ]);

  // Stop any speech when the panel closes, and return focus to the FAB
  // (WCAG 2.4.3 — focus must not be lost when a dialog closes).
  useEffect(() => {
    if (!open) {
      stopSpeaking();
      if (wasOpenRef.current) fabRef.current?.focus();
    }
    wasOpenRef.current = open;
  }, [open ]);

  return (
    <>
      <button
        type="button"
        ref={fabRef}
        className="concierge-fab"
        onClick={openStandard}
        aria-expanded={open}
        aria-label={open ? 'Close Val' : 'Open Val'}
        title="Ask Val… — your Navigator assistant"
      >
        <span aria-hidden="true">{open ? '✕' : '✦'}</span>
      </button>

      {/* The panel stays mounted once first opened (hidden with CSS when
          closed) so the conversation thread survives close/reopen. */}
      {hasOpened && (
        <section
          className={`concierge-panel${open ? '' : ' is-hidden'}`}
          role="dialog"
          aria-modal="false"
          aria-hidden={!open || undefined}
          aria-label={mode === 'onboard' ? 'Val — Guided profile setup' : 'Val — Virtual AI Assistant Lookup'}
        >
          <ConciergeThread key={`${mode}-${initialMessage ?? 'x'}`} mode={mode} initialMessage={initialMessage} opportunityId={opportunityId} onClose={() => setOpen(false)} />
        </section>
      )}
    </>
  );
}
